import { useState, useEffect, useRef } from "react";
import { useAuth, useDateFilter } from "../App";
import Layout from "../components/Layout";
import DateRangePicker from "../components/DateRangePicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { TrendingUp, TrendingDown, Wallet, DollarSign, IceCream, Shirt, Package, Banknote, CreditCard, Users, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { ProductThumbnail } from "../components/ProductThumbnail";
import { BranchSelector, BranchBadge } from "../components/BranchSelector";
import axios from "axios";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ["#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

// Wrapper component to prevent charts from rendering with negative dimensions
const ChartContainer = ({ children, className = "h-72" }) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const checkDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        if (width > 50 && height > 50) {
          setIsReady(true);
        }
      }
    };
    
    // Check immediately
    checkDimensions();
    
    // Check after delays for PWA/mobile scenarios
    const timeouts = [
      setTimeout(checkDimensions, 50),
      setTimeout(checkDimensions, 150),
      setTimeout(checkDimensions, 300),
      setTimeout(checkDimensions, 500),
    ];
    
    // Also check on resize
    window.addEventListener('resize', checkDimensions);
    
    // Intersection observer to check when element becomes visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(checkDimensions, 50);
        }
      });
    }, { threshold: 0.1 });
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      timeouts.forEach(t => clearTimeout(t));
      window.removeEventListener('resize', checkDimensions);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className={className} style={{ minHeight: '200px' }}>
      {isReady ? children : (
        <div className="flex items-center justify-center h-full text-gray-400">
          Cargando gráfico...
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { token } = useAuth();
  const { startDate, endDate } = useDateFilter();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [branchObj, setBranchObj] = useState(null);

  useEffect(() => {
    if (startDate && endDate) {
      // Clear previous data before fetching new data
      setSummary(null);
      setError(null);
      fetchSummary();
    }
  }, [startDate, endDate, branchId]);

  const fetchSummary = async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      const branchQuery = branchId ? `&branch_id=${branchId}` : "";
      const response = await axios.get(
        `${API_URL}/api/dashboard/summary?start_date=${startStr}&end_date=${endStr}${branchQuery}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching summary:", err);
      setError(err.message || "Error al cargar datos");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#FECDD3] rounded-lg p-3 shadow-lg">
          <p className="text-[#4A4A4A] font-medium">{payload[0].name || payload[0].payload.name}</p>
          <p className="text-[#F43F5E] font-mono">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-500 mb-4">Error al cargar datos</p>
          <button 
            onClick={() => fetchSummary()} 
            className="px-4 py-2 bg-[#F43F5E] text-white rounded-lg"
          >
            Reintentar
          </button>
        </div>
      </Layout>
    );
  }

  if (!summary) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Dashboard</h1>
              <BranchBadge branch={branchObj} />
            </div>
            <p className="text-[#6B7280]">
              Resumen financiero del período · <span className="text-[#8B5CF6]">Fuente: Odoo POS</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <BranchSelector value={branchId} onChange={(id, obj) => { setBranchId(id); setBranchObj(obj); }} />
            <DateRangePicker />
          </div>
        </div>

        {/* Stats Cards - 2 columns layout */}
        <div className="grid grid-cols-2 gap-4">
          {/* Row 1: Ingresos Totales | Gastos Operativos */}
          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="total-income-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Ingresos Totales</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#10B981] font-mono mt-1">
                    {formatCurrency(summary?.total_income)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="total-expenses-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Gastos Operativos</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#EF4444] font-mono mt-1">
                    {formatCurrency(summary?.operational_expenses)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#FEE2E2] flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 lg:w-6 lg:h-6 text-[#EF4444]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row 1.5: Ventas Efectivo | Ventas Tarjeta */}
          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="sales-cash-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Ventas Efectivo</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#059669] font-mono mt-1">
                    {formatCurrency(summary?.sales_cash)}
                  </p>
                  <p className="text-[10px] lg:text-xs text-[#6B7280] mt-0.5">
                    {(summary?.orders_cash || 0).toLocaleString()} órden{(summary?.orders_cash || 0) === 1 ? '' : 'es'}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                  <Banknote className="w-5 h-5 lg:w-6 lg:h-6 text-[#059669]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="sales-card-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Ventas Tarjeta</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#2563EB] font-mono mt-1">
                    {formatCurrency(summary?.sales_card)}
                  </p>
                  <p className="text-[10px] lg:text-xs text-[#6B7280] mt-0.5">
                    {(summary?.orders_card || 0).toLocaleString()} órden{(summary?.orders_card || 0) === 1 ? '' : 'es'}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                  <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-[#2563EB]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row 1.75: Órdenes | Ticket Promedio */}
          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="total-orders-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Clientes Atendidos (Órdenes)</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#8B5CF6] font-mono mt-1">
                    {(summary?.total_orders || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#EDE9FE] flex items-center justify-center">
                  <Users className="w-5 h-5 lg:w-6 lg:h-6 text-[#8B5CF6]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="avg-ticket-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Ticket Promedio</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#F97316] font-mono mt-1">
                    {formatCurrency(summary?.avg_ticket)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#FFEDD5] flex items-center justify-center">
                  <Receipt className="w-5 h-5 lg:w-6 lg:h-6 text-[#F97316]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row 2: Balance | Reinversión */}
          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="balance-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Balance</p>
                  <p className={`text-lg lg:text-2xl font-bold font-mono mt-1 ${(summary?.balance || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {formatCurrency(summary?.balance)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                  <Wallet className="w-5 h-5 lg:w-6 lg:h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="extras-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Reinversión</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#F59E0B] font-mono mt-1">
                    {formatCurrency(summary?.extras_expenses)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-[#F59E0B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row 3: Inversión Merch | Gastos Totales */}
          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="merch-cost-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Inversión Merch</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#7C3AED] font-mono mt-1">
                    {formatCurrency(summary?.merch_cost_recovery)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#EDE9FE] flex items-center justify-center">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-[#8B5CF6]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover shadow-sm" data-testid="total-all-expenses-card">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs lg:text-sm">Gastos Totales</p>
                  <p className="text-lg lg:text-2xl font-bold text-[#6B7280] font-mono mt-1">
                    {formatCurrency(summary?.total_expenses)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                  <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-[#6B7280]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Business Profitability Section */}
        <Card className="bg-white border-[#FECDD3] shadow-sm" data-testid="business-profitability">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Rentabilidad por Negocio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] border border-[#10B981]/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/60 flex items-center justify-center">
                    <IceCream className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <span className="text-[#047857] font-medium">Heladería 🍦</span>
                </div>
                <p className={`text-3xl font-bold font-mono ${(summary?.heladeria_profit || 0) < 0 ? 'text-[#EF4444]' : 'text-[#047857]'}`}>
                  {formatCurrency(summary?.heladeria_profit)}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] border border-[#F59E0B]/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/60 flex items-center justify-center">
                    <Shirt className="w-5 h-5 text-[#D97706]" />
                  </div>
                  <span className="text-[#B45309] font-medium">Merch 👕</span>
                </div>
                <p className={`text-3xl font-bold font-mono ${(summary?.merch_profit || 0) < 0 ? 'text-[#EF4444]' : 'text-[#B45309]'}`}>
                  {formatCurrency(summary?.merch_profit)}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-[#EDE9FE] to-[#DDD6FE] border border-[#8B5CF6]/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/60 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <span className="text-[#7C3AED] font-medium">Margen Total 💰</span>
                </div>
                <p className={`text-3xl font-bold font-mono ${((summary?.heladeria_profit || 0) + (summary?.merch_profit || 0)) < 0 ? 'text-[#EF4444]' : 'text-[#7C3AED]'}`}>
                  {formatCurrency((summary?.heladeria_profit || 0) + (summary?.merch_profit || 0))}
                </p>
                <p className="text-[#8B5CF6] text-sm mt-1">Heladería + Merch</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card className="bg-white border-[#FECDD3] shadow-sm" data-testid="monthly-trend-chart">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Tendencia Mensual (Últimos 6 meses)</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.monthly_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} stroke="#6B7280" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Income by Internal Product */}
        {summary?.income_by_category?.length > 0 && (
          <Card className="bg-white border-[#FECDD3] shadow-sm" data-testid="income-by-product-chart">
            <CardHeader>
              <CardTitle className="text-[#4A4A4A] font-['Manrope']">Ingresos por Producto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {summary.income_by_category.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-[#F0FDF4] rounded-xl border border-[#D1FAE5]">
                    <ProductThumbnail image_url={item.image_url} name={item.name} size="lg" rounded="rounded-xl" />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color || CHART_COLORS[index % CHART_COLORS.length] }}
                        ></div>
                        <span className="text-[#4A4A4A] font-semibold truncate">{item.name}</span>
                      </div>
                      {typeof item.units === 'number' && item.units > 0 && (
                        <span className="text-xs text-[#6B7280] mt-0.5">
                          {item.units} {item.units === 1 ? 'pieza' : 'piezas'}
                        </span>
                      )}
                      <span className="font-mono text-[#10B981] text-lg font-semibold mt-1">
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default DashboardPage;
